/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  useState,
  useEffect,
  ChangeEvent,
  useMemo,
  useCallback,
} from "react";
import {
  Form,
  Table,
  Button,
  Offcanvas,
  Spinner,
  Badge,
  Modal,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import { Bounce, toast } from "react-toastify";
import notificationSound from "../assets/sound/notification.wav";
import { getReportName, logHistory, toTimestamp } from "../utilities/functions";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface ReportData {
  id_report: number;
  turn_report: number;
  date_creation: string;
  id_user: number;
  nom_user: string;
  prenom_user: string;
  date_debut: string;
  date_fin: string;
  PSN: string | null;
  immatriculation_vehicule: string | null;
  state_report: number;
  type_report: string;
  id_dispositif: number;
}

interface TotalsResponse {
  total: number;
}

const SEARCH_OPTIONS = [
  { value: "vehicle", labelKey: "Vehicle" },
  { value: "user", labelKey: "User" },
  { value: "turn", labelKey: "turn" },
] as const;

export function Reports() {
  const { translate } = useTranslate();
  const navigate = useNavigate();

  const user_id = localStorage.getItem("userid");

  const [listReports, setListReports] = useState<ReportData[]>([]);
  const [selectedReportsData, setSelectedReportsData] = useState<ReportData[]>(
    []
  );
  const [selectedItems, setSelectedItems] = useState<ReportData[]>([]);

  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [loading, setLoading] = useState(true);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [sort, setSort] = useState<"ASC" | "DESC">("DESC");
  const [column, setColumn] = useState("turn_report");

  const [search, setSearch] = useState("");
  const [type, setType] = useState<"vehicle" | "user" | "turn">("vehicle");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);

      setTimeout(() => {
        setCopiedId(null);
      }, 1500);
    } catch (error) {
      console.error("Erreur copie clipboard :", error);
    }
  };

  const openDeleteModal = () => {
    if (selectedItems.length === 0) return;
    setShowDeleteModal(true);
  };

  const selectAll = useMemo(() => {
    return (
      listReports.length > 0 &&
      listReports.every((row) =>
        selectedItems.some((selected) => selected.id_report === row.id_report)
      )
    );
  }, [listReports, selectedItems]);

  const placeholderText = useMemo(() => {
    const found = SEARCH_OPTIONS.find((opt) => opt.value === type);
    return `${translate("Search by")} ${translate(
      found?.labelKey || "Vehicle"
    )}`;
  }, [type, translate]);

  const notifyCalculatedReports = useCallback(
    (idReports: number[]) => {
      if (idReports.length === 0) return;

      const audio = new Audio(notificationSound);
      audio.play().catch((error) => {
        console.error("Erreur lecture audio notification:", error);
      });

      toast.success(
        translate(
          `Reports with ids ${idReports.join(", ")} calculated successfully!`
        ),
        {
          position: "bottom-right",
          autoClose: 2400,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          transition: Bounce,
        }
      );
    },
    [translate]
  );

  const confirmDeleteSelectedReports = async () => {
    if (selectedItems.length === 0) return;

    try {
      setDeleteLoading(true);

      const uniqueGroups = Array.from(
        new Map(
          selectedItems.map((item) => [
            `${item.turn_report}-${item.id_dispositif}`,
            {
              turn_report: item.turn_report,
              id_dispositif: item.id_dispositif,
            },
          ])
        ).values()
      );

      for (const group of uniqueGroups) {
        const response = await fetch(`${backendUrl}/api/reports/delete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(group),
        });

        if (!response.ok) {
          throw new Error(`Erreur suppression: ${response.status}`);
        }
      }

await logHistory(
          user_id,
          "Delete",
          "Reports",
          "delete",
          `Delete Reports: ${uniqueGroups.map((g) => `${g.turn_report}-${g.id_dispositif}`).join(", ")}`
        );

      toast.success(translate("Reports deleted successfully"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });

      setSelectedItems([]);
      setShowDeleteModal(false);
      await fetchReportData(currentPage, limit, false);
    } catch (error) {
      console.error(error);
      toast.error(translate("Error deleting reports"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const getDifferentReportIds = useCallback(
    (oldList: ReportData[], newList: ReportData[]) => {
      const changedIds: number[] = [];

      for (const oldItem of oldList) {
        const matching = newList.find(
          (newItem) => newItem.id_report === oldItem.id_report
        );

        if (
          matching &&
          oldItem.state_report !== matching.state_report &&
          matching.state_report === 1
        ) {
          changedIds.push(oldItem.id_report);
        }
      }

      return changedIds;
    },
    []
  );

  const fetchReportData = useCallback(
    async (pageToLoad = currentPage, limitToLoad = limit, silent = false) => {
      try {
        if (!silent) setLoading(true);

        const totalRes = await fetch(`${backendUrl}/api/rapport/totalpage`, {
          method: "POST",
          mode: "cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            search,
            type,
            dateFrom,
            dateTo,
            status: statusFilter,
            id_user: user_id,
          }),
        });

        if (!totalRes.ok) {
          throw new Error(`Erreur total reports: ${totalRes.status}`);
        }

        const totalData: TotalsResponse[] = await totalRes.json();
        const newTotal =
          Array.isArray(totalData) && totalData.length > 0
            ? Number(totalData[0].total)
            : 0;

        setTotal(newTotal);
        setPageCount(Math.ceil(newTotal / limitToLoad));

        const res = await fetch(`${backendUrl}/api/rapports/search`, {
          method: "POST",
          mode: "cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            page: pageToLoad,
            limit: limitToLoad,
            column,
            sort,
            search,
            type,
            dateFrom,
            dateTo,
            status: statusFilter,
            id_user: user_id,
          }),
        });

        if (res.status === 404) {
          setListReports([]);
          setSelectedItems([]);
          return [];
        }

        if (!res.ok) {
          throw new Error(`Erreur récupération reports: ${res.status}`);
        }

        const data: ReportData[] = await res.json();
        const safeData = Array.isArray(data) ? data : [];

        setListReports(safeData);

        setSelectedItems((prev) =>
          prev.filter((selected) =>
            safeData.some((row) => row.id_report === selected.id_report)
          )
        );

        return safeData;
      } catch (error) {
        console.error(error);
        setListReports([]);
        setSelectedItems([]);
        setTotal(0);
        setPageCount(0);

        if (!silent) {
          toast.error(translate("Error loading reports"), {
            position: "bottom-right",
            autoClose: 2500,
            transition: Bounce,
          });
        }

        return [];
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [
      currentPage,
      limit,
      column,
      sort,
      search,
      type,
      dateFrom,
      dateTo,
      statusFilter,
      user_id,
      translate,
    ]
  );

  const checkAndUpdateReportStatus = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/rapports/search`, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page: currentPage,
          limit,
          column,
          sort,
          search,
          type,
          dateFrom,
          dateTo,
          status: statusFilter,
          id_user: user_id,
        }),
      });

      if (res.status === 404) {
        if (listReports.length > 0) {
          setListReports([]);
          setSelectedItems([]);
        }
        return;
      }

      if (!res.ok) return;

      const newData: ReportData[] = await res.json();
      const safeData = Array.isArray(newData) ? newData : [];

      const changedIds = getDifferentReportIds(listReports, safeData);

      if (changedIds.length > 0) {
        notifyCalculatedReports(changedIds);
      }

      setListReports(safeData);

      setSelectedItems((prev) =>
        prev.filter((selected) =>
          safeData.some((row) => row.id_report === selected.id_report)
        )
      );
    } catch (error) {
      console.error("Erreur mise à jour cycle rapports:", error);
    }
  }, [
    currentPage,
    limit,
    column,
    sort,
    search,
    type,
    dateFrom,
    dateTo,
    statusFilter,
    user_id,
    listReports,
    getDifferentReportIds,
    notifyCalculatedReports,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, type, dateFrom, dateTo, statusFilter, limit, column, sort]);

  useEffect(() => {
    fetchReportData(currentPage, limit, false);
  }, [
    currentPage,
    limit,
    column,
    sort,
    search,
    type,
    dateFrom,
    dateTo,
    statusFilter,
    fetchReportData,
  ]);

  useEffect(() => {
    const hasProcessingReports = listReports.some(
      (report) => report.state_report === 2
    );

    if (!hasProcessingReports) return;

    const intervalId = setInterval(() => {
      checkAndUpdateReportStatus();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [listReports, checkAndUpdateReportStatus]);

  const handlePageClick = (data: { selected: number }) => {
    setCurrentPage(data.selected + 1);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newValue = parseInt(event.target.value, 10);
    setLimit(newValue);
  };

  const handleSelectAll = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const isChecked = target.checked;
    if (isChecked) {
      setSelectedItems(listReports);
    } else {
      setSelectedItems([]);
    }
  };

  const handleCheckboxChange = (
    { target }: ChangeEvent<HTMLInputElement>,
    report: ReportData
  ) => {
    const isChecked = target.checked;

    setSelectedItems((prevSelectedItems) => {
      if (isChecked) {
        const exists = prevSelectedItems.some(
          (item) => item.id_report === report.id_report
        );
        return exists ? prevSelectedItems : [...prevSelectedItems, report];
      }

      return prevSelectedItems.filter(
        (selectedItem) => selectedItem.id_report !== report.id_report
      );
    });
  };

  const handleSort = (nextColumn: string) => {
    if (column === nextColumn) {
      setSort((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setColumn(nextColumn);
      setSort("ASC");
    }
  };

  const showTypeReportList = async (
    turn_report: number,
    id_dispositif: number
  ) => {
    try {
      setDrawerLoading(true);
      setShowDrawer(true);

      const res = await fetch(
        `${backendUrl}/api/reports/byTurn/${turn_report}/${id_dispositif}`,
        { mode: "cors" }
      );

      if (!res.ok) {
        throw new Error(`Erreur récupération détail reports: ${res.status}`);
      }

      const data: ReportData[] = await res.json();
      setSelectedReportsData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setSelectedReportsData([]);
      toast.error(translate("Error loading report details"), {
        position: "bottom-right",
        autoClose: 2500,
        transition: Bounce,
      });
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleItemClick = (report: ReportData) => {
    navigate(
      `/report/${report.id_report}/${report.turn_report}/${report.type_report}/${report.id_dispositif}`
    );
  };

  const clearSearch = () => {
    setSearch("");
  };

  const resetFilters = () => {
    setSearch("");
    setType("vehicle");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    setColumn("turn_report");
    setSort("DESC");
    setCurrentPage(1);
  };

  const renderStatusBadge = (item: ReportData) => {
    let bg = "#fef3c7";
    let color = "#92400e";
    let label = translate("pending");
    let iconClass = "las la-clock";

    if (item.state_report === 1) {
      bg = "#dcfce7";
      color = "#166534";
      label = translate("calculated");
      iconClass = "las la-check-circle";
    } else if (item.state_report === 2) {
      bg = "#dbeafe";
      color = "#1d4ed8";
      label = translate("processing");
      iconClass = "las la-spinner la-spin";
    } else if (item.state_report === 0) {
      bg = "#fee2e2";
      color = "#b91c1c";
      label = translate("empty");
      iconClass = "las la-times-circle";
    }

    return (
      <button
        type="button"
        onClick={() => showTypeReportList(item.turn_report, item.id_dispositif)}
        style={{
          border: "none",
          background: bg,
          color,
          padding: "6px 12px",
          borderRadius: "999px",
          fontSize: "12px",
          fontWeight: 600,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <i className={iconClass}></i>
        {label}
      </button>
    );
  };

  return (
    <>
      <div className="row">
        <div className="col-sm-12 col-md-6">
          <h4 className="mb-3">
            <i
              className="ri ri-bar-chart-line"
              data-rel="bootstrap-tooltip"
              title="Rapports"
              style={{ color: "#f59e0b", fontSize: "31px" }}
            ></i>{" "}
            {translate("Rapports")} ({total})
          </h4>
        </div>

        <div
          className="col-sm-12 col-md-6"
          style={{
            textAlign: "right",
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <Link
            to="/new-report"
            className="btn"
            style={{
              background: "#000",
              border: "1px solid #000",
              color: "#fff",
              borderRadius: "10px",
              height: "44px",
              padding: "0 18px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textDecoration: "none",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#111";
              e.currentTarget.style.border = "1px solid #111";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#000";
              e.currentTarget.style.border = "1px solid #000";
            }}
          >
            <i className="las la-plus"></i>
            {translate("New Report")}
          </Link>

          <Button
            onClick={openDeleteModal}
            disabled={selectedItems.length === 0 || deleteLoading}
            style={{
              background: "#f59e0b",
              border: "1px solid #f59e0b",
              color: "#fff",
              borderRadius: "10px",
              height: "44px",
              padding: "0 18px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "all 0.2s ease",
              opacity: selectedItems.length === 0 || deleteLoading ? 0.65 : 1,
            }}
            onMouseEnter={(e) => {
              if (!(selectedItems.length === 0 || deleteLoading)) {
                e.currentTarget.style.background = "#d97706";
                e.currentTarget.style.border = "1px solid #d97706";
              }
            }}
            onMouseLeave={(e) => {
              if (!(selectedItems.length === 0 || deleteLoading)) {
                e.currentTarget.style.background = "#f59e0b";
                e.currentTarget.style.border = "1px solid #f59e0b";
              }
            }}
          >
            {deleteLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                {translate("Deleting")}
              </>
            ) : (
              <>
                <i className="las la-trash"></i>
                {translate("Delete")}
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 16,
              boxShadow: "0 4px 18px rgba(15,23,42,0.06)",
              marginBottom: 16,
            }}
          >
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <Form.Label>{translate("search")}</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="text"
                    placeholder={placeholderText}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <Button variant="light" onClick={clearSearch}>
                      <i className="las la-times-circle"></i>
                    </Button>
                  )}
                </div>
              </div>

              <div className="col-md-1">
                <Form.Label>{translate("Search by")}</Form.Label>
                <Form.Select
                  value={type}
                  onChange={(e) =>
                    setType(e.target.value as "vehicle" | "user" | "turn")
                  }
                >
                  {SEARCH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {translate(option.labelKey)}
                    </option>
                  ))}
                </Form.Select>
              </div>

      

              <div className="col-md-2">
                <Form.Label>{translate("date_from")}</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <Form.Label>{translate("date_to")}</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>

                      <div className="col-md-1">
                <Form.Label>{translate("State")}</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">{translate("All")}</option>
                  <option value="1">{translate("calculated")}</option>
                  <option value="2">{translate("processing")}</option>
                  <option value="0">{translate("empty")}</option>
                </Form.Select>
              </div>

              <div className="col-md-1">
                <Form.Label>{translate("Show")}</Form.Label>
                <Form.Select value={limit} onChange={handleLimitChange}>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                </Form.Select>
              </div>

              <div className="col-md-2 d-grid">
                <Button variant="outline-secondary" onClick={resetFilters}>
                  {translate("Reset")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 4px 18px rgba(15,23,42,0.06)",
            }}
          >
            <Table hover responsive className="mb-0">
              <thead style={{ background: "#f8fafc" }}>
                <tr>
                  <th style={{ width: 45 }}>
                    <div className="checkbox d-inline-block">
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        id="checkbox-all-reports"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      <label
                        htmlFor="checkbox-all-reports"
                        className="mb-0"
                      ></label>
                    </div>
                  </th>

                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("turn_report")}
                  >
                    {translate("turn")}{" "}
                    {column === "turn_report" && (sort === "ASC" ? "▲" : "▼")}
                  </th>

                  <th>{translate("Vehicle")}</th>
                  <th>{translate("user_name")}</th>

                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("date_creation")}
                  >
                    {translate("Creation date")}{" "}
                    {column === "date_creation" &&
                      (sort === "ASC" ? "▲" : "▼")}
                  </th>

                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("date_debut")}
                  >
                    {translate("Start date")}{" "}
                    {column === "date_debut" && (sort === "ASC" ? "▲" : "▼")}
                  </th>

                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("date_fin")}
                  >
                    {translate("End date")}{" "}
                    {column === "date_fin" && (sort === "ASC" ? "▲" : "▼")}
                  </th>

                  <th>{translate("State")}</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      <Spinner animation="border" />
                    </td>
                  </tr>
                ) : listReports.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      {translate("No reports found")}
                    </td>
                  </tr>
                ) : (
                  listReports.map((item) => (
                    <tr key={`${item.turn_report}-${item.id_dispositif}`}>
                      <td>
                        <div className="checkbox d-inline-block">
                          <input
                            type="checkbox"
                            className="checkbox-input"
                            id={`checkbox-${item.id_report}`}
                            checked={selectedItems.some(
                              (selectedItem) =>
                                selectedItem.id_report === item.id_report
                            )}
                            onChange={(event) =>
                              handleCheckboxChange(event, item)
                            }
                          />
                          <label
                            htmlFor={`checkbox-${item.id_report}`}
                            className="mb-0"
                          ></label>
                        </div>
                      </td>

                      <td
                        style={{
                          cursor: "pointer",
                          position: "relative",
                          padding: "10px",
                        }}
                        title={translate("click_to_copy_turn")}
                        onClick={() =>
                          copyToClipboard(
                            item.turn_report.toString(),
                            `turn-${item.id_report}`
                          )
                        }
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              color:
                                copiedId === `turn-${item.id_report}`
                                  ? "#22c55e"
                                  : "#0f172a",
                            }}
                          >
                            {item.turn_report}
                          </span>

                          <i
                            className="las la-copy"
                            style={{
                              fontSize: "14px",
                              color:
                                copiedId === `turn-${item.id_report}`
                                  ? "#22c55e"
                                  : "#94a3b8",
                            }}
                          ></i>
                        </div>

                        {copiedId === `turn-${item.id_report}` && (
                          <span
                            style={{
                              position: "absolute",
                              top: "-24px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              background: "#22c55e",
                              color: "#fff",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              fontSize: "11px",
                              boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                            }}
                          >
                            ✔ {translate("turn_copied")}
                          </span>
                        )}
                      </td>

                      <td
                        style={{
                          cursor: item.immatriculation_vehicule
                            ? "pointer"
                            : "default",
                          position: "relative",
                          padding: "10px",
                        }}
                        title={translate("click_to_copy")}
                        onClick={() => {
                          if (item.immatriculation_vehicule) {
                            copyToClipboard(
                              item.immatriculation_vehicule,
                              `vehicule-${item.id_report}`
                            );
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 500,
                              color:
                                copiedId === `vehicule-${item.id_report}`
                                  ? "#22c55e"
                                  : "#0f172a",
                            }}
                          >
                            {item.immatriculation_vehicule}
                          </span>

                          <i
                            className="las la-copy"
                            style={{
                              fontSize: "14px",
                              color:
                                copiedId === `vehicule-${item.id_report}`
                                  ? "#22c55e"
                                  : "#94a3b8",
                            }}
                          ></i>
                        </div>

                        {copiedId === `vehicule-${item.id_report}` && (
                          <span
                            style={{
                              position: "absolute",
                              top: "-24px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              background: "#22c55e",
                              color: "#fff",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              fontSize: "11px",
                              boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                            }}
                          >
                            ✔ {translate("copied")}
                          </span>
                        )}
                      </td>

                      <td>
                        {`${item.nom_user || ""} ${item.prenom_user || ""}`.trim() ||
                          "-"}
                      </td>
                      <td>{toTimestamp(item.date_creation)}</td>
                      <td>{toTimestamp(item.date_debut)}</td>
                      <td>{toTimestamp(item.date_fin)}</td>
                      <td>{renderStatusBadge(item)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>

        <div className="col-md-6 d-flex align-items-center mt-3">
          <span>
            {translate("Displaying")} {listReports.length}{" "}
            {translate("out of")} {total}
          </span>
        </div>

        <div className="col-md-6 d-flex justify-content-end mt-3">
          <ReactPaginate
            previousLabel={translate("previous")}
            nextLabel={translate("next")}
            breakLabel={"..."}
            pageCount={pageCount}
            forcePage={currentPage - 1}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-center mb-0"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
          />
        </div>
      </div>

      <Offcanvas
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        placement="end"
        style={{ width: 460 }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{translate("Report List")}</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {drawerLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : selectedReportsData.length > 0 ? (
            <>
              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: 14,
                  padding: 16,
                  marginBottom: 16,
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  <strong>{translate("Turn Report")}:</strong>{" "}
                  {selectedReportsData[0].turn_report}
                </div>
                <div>
                  <strong>{translate("Device")}:</strong>{" "}
                  {selectedReportsData[0].id_dispositif}
                </div>
              </div>

              <div style={{ display: "grid", gap: 12 }}>
                {selectedReportsData.map((report) => {
                  const isCalculated = report.state_report === 1;

                  return (
                    <div
                      key={report.id_report}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 14,
                        padding: 14,
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 10,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>
                            [{report.type_report}]{" "}
                            {getReportName(report.type_report, translate)}
                          </div>
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            ID: {report.id_report}
                          </div>
                        </div>

                        <Badge bg={isCalculated ? "success" : "secondary"}>
                          {isCalculated
                            ? translate("Open")
                            : translate("Unavailable")}
                        </Badge>
                      </div>

                      <div className="d-grid">
                        <Button
                          variant={
                            isCalculated
                              ? "outline-primary"
                              : "outline-secondary"
                          }
                          disabled={!isCalculated}
                          onClick={() => handleItemClick(report)}
                        >
                          <i className="las la-eye mr-2"></i>
                          {translate("View report")}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p>{translate("No data available for selected turn report.")}</p>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{translate("Delete reports")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p style={{ marginBottom: 10 }}>
            {translate("Are you sure you want to delete the selected reports?")}
          </p>

          <div
            style={{
              background: "#f8fafc",
              borderRadius: 12,
              padding: 12,
              fontSize: 13,
              color: "#475569",
            }}
          >
            <strong>{selectedItems.length}</strong>{" "}
            {translate("report(s) selected")}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleteLoading}
          >
            {translate("Cancel")}
          </Button>

          <Button
            variant="danger"
            onClick={confirmDeleteSelectedReports}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                {translate("Deleting")}
              </>
            ) : (
              <>
                <i className="las la-trash mr-2"></i>
                {translate("Confirm delete")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Reports;